import React from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox,
  FormControlLabel, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  PERMISSION_ACTIONS,
  MODULE_LABELS,
  ACTION_LABELS,
  PERMISSION_MODULE_GROUPS,
  PERMISSION_GROUP_LABELS,
  PERMISSION_SUB_MODULES,
  TENANT_PERMISSION_MODULES,
  PermissionModule,
} from '../../constants/permissions';

type PermMap = Record<string, Record<string, boolean>>;

interface PermissionMatrixProps {
  permissions: PermMap;
  onChange: (permissions: PermMap) => void;
  readOnly?: boolean;
}

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({ permissions, onChange, readOnly }) => {
  const togglePerm = (mod: string, action: string) => {
    if (readOnly) return;
    onChange({
      ...permissions,
      [mod]: { ...permissions[mod], [action]: !permissions[mod]?.[action] },
    });
  };

  const setAllForModule = (mod: string, value: boolean) => {
    if (readOnly) return;
    onChange({
      ...permissions,
      [mod]: PERMISSION_ACTIONS.reduce((acc, action) => {
        acc[action] = value;
        return acc;
      }, {} as Record<string, boolean>),
    });
  };

  const setAllForGroup = (mods: PermissionModule[], value: boolean) => {
    if (readOnly) return;
    const next = { ...permissions };
    mods.forEach((mod) => {
      next[mod] = PERMISSION_ACTIONS.reduce((acc, action) => {
        acc[action] = value;
        return acc;
      }, {} as Record<string, boolean>);
    });
    onChange(next);
  };

  const renderModuleRow = (mod: PermissionModule, nested = false) => (
    <Box key={mod} sx={{ mb: 1, p: 1, bgcolor: nested ? '#f1f5f9' : '#f8fafc', borderRadius: 1, ml: nested ? 2 : 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
        <Typography variant="subtitle2" fontWeight={700} fontSize={nested ? 11 : 12}>{MODULE_LABELS[mod]}</Typography>
        {!readOnly && (
          <Box>
            <Button size="small" sx={{ fontSize: 10, minWidth: 0, mr: 0.5 }} onClick={() => setAllForModule(mod, true)}>All</Button>
            <Button size="small" sx={{ fontSize: 10, minWidth: 0 }} onClick={() => setAllForModule(mod, false)}>None</Button>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25 }}>
        {PERMISSION_ACTIONS.map((action) => (
          <FormControlLabel
            key={action}
            control={
              <Checkbox
                checked={!!permissions[mod]?.[action]}
                onChange={() => togglePerm(mod, action)}
                size="small"
                disabled={readOnly}
              />
            }
            label={ACTION_LABELS[action] || action}
            sx={{ mr: 0.5, '& .MuiFormControlLabel-label': { fontSize: 11 } }}
          />
        ))}
      </Box>
      {(PERMISSION_SUB_MODULES[mod] || [])
        .filter((sub) => TENANT_PERMISSION_MODULES.includes(sub))
        .map((sub) => renderModuleRow(sub, true))}
    </Box>
  );

  return (
    <Box>
      {Object.entries(PERMISSION_MODULE_GROUPS).map(([group, mods]) => {
        const visibleMods = mods.filter((m) => TENANT_PERMISSION_MODULES.includes(m));
        if (!visibleMods.length) return null;
        return (
          <Accordion
            key={group}
            disableGutters
            elevation={0}
            sx={{
              mb: 0.5,
              border: '1px solid #e2e8f0',
              borderRadius: '4px !important',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={{ minHeight: 36, py: 0, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Typography fontWeight={700} fontSize={12}>{PERMISSION_GROUP_LABELS[group] || group}</Typography>
                {!readOnly && (
                  <Box onClick={(e) => e.stopPropagation()}>
                    <Button size="small" sx={{ fontSize: 10, minWidth: 0, mr: 0.5 }} onClick={() => setAllForGroup(visibleMods, true)}>All</Button>
                    <Button size="small" sx={{ fontSize: 10, minWidth: 0 }} onClick={() => setAllForGroup(visibleMods, false)}>None</Button>
                  </Box>
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1, px: 1.5 }}>
              {visibleMods.map((mod) => renderModuleRow(mod))}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export const normalizePermissions = (input?: PermMap): PermMap => {
  const base: PermMap = {};
  TENANT_PERMISSION_MODULES.forEach((mod) => {
    base[mod] = PERMISSION_ACTIONS.reduce((actions, action) => {
      actions[action] = Boolean(input?.[mod]?.[action]);
      return actions;
    }, {} as Record<string, boolean>);
  });
  return base;
};

export const countViewModules = (permissions: PermMap) =>
  TENANT_PERMISSION_MODULES.filter((m) => permissions[m]?.view).length;
